/*
  全站唯一的 SEO / AEO / GEO 事实源。

  为什么单独一个文件：
    这个站是 Vue2 SPA，没有 SSR。爬虫要看的静态副本（public/index.html 里的
    预渲染块、JSON-LD、sitemap、llms.txt）没法从组件里读，只能由
    scripts/generate-seo.mjs 在构建前生成。如果页面上的可见文案和静态副本各写一份，
    迟早会出现「页面上写 0.02 ETH、结构化数据里写 0.05 ETH」——结构化数据和
    可见内容不一致是会被算法判罚的那种错。所以两边都从这里读。

  改文案只改这个文件，然后 `yarn seo`（build 前会自动跑）重新生成静态文件。
*/

export const SITE = {
  /** 本站自己的地址。主站 nav 里「任意链发币」就指向这里，不要写成主站。 */
  origin: 'https://token.gtokentool.com',
  brand: 'GTokenTool',
  /** 用于 title / JSON-LD 的完整名称 */
  brandFull: 'GTokenTool 任意链发币',
  /** 姊妹站：主站（EVM 全套工具）+ 三条单链站 */
  mainSite: 'https://www.gtokentool.com/',
  siblings: [
    { label: 'GTokenTool Solana', href: 'https://sol.gtokentool.com/' },
    { label: 'GTokenTool TON', href: 'https://ton.gtokentool.com/' },
    { label: 'GTokenTool Sui', href: 'https://sui.gtokentool.com/' },
    { label: 'GTokenTool Tron', href: 'https://tron.gtokentool.com/' },
  ],
  social: [
    {
      label: { zh: 'Telegram 中文社区', en: 'Telegram (Chinese)' },
      href: 'https://t.me/gtokentool',
    },
    {
      label: { zh: 'Telegram 英文社区', en: 'Telegram (English)' },
      href: 'https://t.me/GToken_EN',
    },
    {
      label: { zh: 'X（推特）@gtokentool', en: 'X (Twitter) @gtokentool' },
      href: 'https://x.com/gtokentool',
    },
    {
      label: { zh: 'YouTube 频道', en: 'YouTube channel' },
      href: 'https://www.youtube.com/@GTokenTool',
    },
    {
      label: { zh: 'GitHub', en: 'GitHub' },
      href: 'https://github.com/gtokentool',
    },
  ],
  guides: {
    label: { zh: '使用文档', en: 'Guides' },
    href: {
      zh: 'https://www.gtokentool.com/guide',
      en: 'https://www.gtokentool.com/en/guide',
    },
  },
  /** 代币构造函数里转发部署费的收款地址，跟 HomeView.vue 里的 FEE_ADDRESS 是同一个 */
  feeAddress: '0x7d1b5a54b17a4D2bC2CEA69ae29d1A441020bbE1',
  /** 静态 HTML 与 canonical 用中文（站内默认语言），英文有独立的 /en/ 页面 */
  defaultLocale: 'zh',
  locales: [
    { code: 'zh-CN', param: 'zh', htmlLang: 'zh-CN' },
    { code: 'en', param: 'en', htmlLang: 'en' },
  ],
}

/*
  链名与原生币单位，两个用途：
    1. 带 fee 的进「常见链的部署费」表 —— 这些数值必须与 src/views/config/price.js
       一致，scripts/generate-seo.mjs 会逐条核对，对不上直接构建失败；
    2. 不带 fee 的只在费用行上标出「你现在连的是哪条链、费用是什么单位」，
       不进费用表。名字没有十足把握的宁可不写，也不往上编。

  费用行上永远同时显示 chainId：名字是给人看的辅助信息，ID 是准的。
*/
export const CHAINS = [
  // —— 价目表里的常见链（fee 必须与 price.js 一致）
  { id: 1, name: 'Ethereum', unit: 'ETH', fee: '0.02' },
  /* BNB Chain 当前 0.05 BNB；改价时这里必须跟着 price.js 一起改 */
  { id: 56, name: 'BNB Chain', unit: 'BNB', fee: '0.05' },
  { id: 137, name: 'Polygon', unit: 'POL', fee: '50' },
  { id: 42161, name: 'Arbitrum One', unit: 'ETH', fee: '0.02' },
  { id: 8453, name: 'Base', unit: 'ETH', fee: '0.02' },
  { id: 59144, name: 'Linea', unit: 'ETH', fee: '0.01' },
  { id: 534352, name: 'Scroll', unit: 'ETH', fee: '0.02' },
  { id: 5000, name: 'Mantle', unit: 'MNT', fee: '50' },
  { id: 130, name: 'Unichain', unit: 'ETH', fee: '0.02' },
  { id: 480, name: 'World Chain', unit: 'ETH', fee: '0.02' },
  { id: 146, name: 'Sonic', unit: 'S', fee: '200' },
  { id: 204, name: 'opBNB', unit: 'BNB', fee: '0.1' },
  // —— 只在费用行上做标识，不进费用表
  { id: 97, name: 'BNB Chain Testnet', unit: 'tBNB' },
  { id: 81457, name: 'Blast', unit: 'ETH' },
  { id: 25, name: 'Cronos', unit: 'CRO' },
  { id: 66, name: 'OKX Chain', unit: 'OKT' },
  { id: 128, name: 'HECO', unit: 'HT' },
  { id: 369, name: 'PulseChain', unit: 'PLS' },
  { id: 1030, name: 'Conflux', unit: 'CFX' },
  { id: 1116, name: 'Core', unit: 'CORE' },
  { id: 1329, name: 'Sei', unit: 'SEI' },
  { id: 143, name: 'Monad', unit: 'MON' },
  { id: 109, name: 'Shibarium', unit: 'BONE' },
  { id: 4200, name: 'Merlin', unit: 'BTC' },
  { id: 9745, name: 'Plasma', unit: 'XPL' },
  { id: 33139, name: 'ApeChain', unit: 'APE' },
]

/** 费用表只登记核对过数值的那些链 */
export const CHAIN_FEES = CHAINS.filter((chain) => chain.fee)

/** 价目表里没有的链，代码里按 1 个原生币收费（见 HomeView.vue 的 concent） */
export const UNLISTED_CHAIN_FEE = '1'

export const CONTENT = {
  zh: {
    /*
      标题按「主关键词 - 一句说清做什么 | 品牌」写：
      前半段是搜索词（任意链发币 / 任何 EVM 链），中段是动作与范围，
      品牌放最后。整条控制在 30 个汉字宽度以内，搜索结果里不会被截断成半句。
      注意别跟主站抢词：主站负责「已收录链的创建代币」，这里只讲「任意链 / 新链」。
    */
    title: '任意链发币 - 任何 EVM 链一键部署代币 | GTokenTool',
    h1: '任意链发币',
    description:
      'GTokenTool 任意链发币：连接钱包、切到目标链，选标准代币或纯手续费代币，填名称、符号、精度、总量即可部署。字节码直接由你的钱包部署，不依赖平台合约，新链、冷门链也能发。',
    /** 直接回答：放在 h1 之后，40-60 字，AEO 抓的就是这一段 */
    answer:
      '任意链发币是 GTokenTool 的站外通道：钱包切到哪条 EVM 兼容链，就在哪条链上发代币。合约字节码直接由你的钱包部署，不经过任何平台工厂合约，所以主站还没收录的新链、冷门链也能用。费用按链固定收取，随部署交易的 value 一起付给平台收款地址，部署成功即已付费。',
    /** 底部折叠面板的标题（用户点开才展开，默认不占视线） */
    panelTitle: '使用步骤与常见问题',
    factsTitle: '一眼看清',
    /*
      facts 只进 JSON-LD 的 featureList，不在页面上铺一张表 ——
      右边那张「这条通道能做什么」已经把同类信息讲完了，再列一遍是重复。
    */
    facts: [
      { label: '支持范围', value: '任何 EVM 兼容链，以钱包能切到的链为准' },
      { label: '部署方式', value: '你的钱包直接部署字节码，不依赖平台合约' },
      { label: '费用', value: '按链固定收费，随部署交易的 value 一起支付' },
      { label: '代币模式', value: '标准代币 / 纯手续费代币' },
      { label: '合约所有权', value: '部署时填的管理员地址，默认是你的钱包' },
      { label: '源码', value: 'Solidity 0.8.6 · MIT · 页面上可直接复制源码与构造参数' },
    ],
    stepsTitle: '怎么在任意链上发一个代币',
    steps: [
      '连接钱包，并把网络切到你要发币的那条链。',
      '选代币模式，填名称、符号、精度、总量和管理员地址。',
      '选择纯手续费代币时，再补填营销税、销毁税和营销钱包。',
      '点「创建代币」，在钱包弹窗里确认交易，费用随这笔交易一起付。',
      '上链后复制合约地址，去那条链自己的 DEX 加池、交易。',
    ],
    faqTitle: '常见问题',
    faq: [
      {
        q: '哪些链可以发币？',
        a: '任何 EVM 兼容链都可以：钱包能切到哪条链，就能在哪条链上发。因为这个通道不依赖平台合约，主站还没收录的新链、冷门链同样可以发币。',
      },
      {
        q: '发一个代币要多少钱？',
        a: '费用按链固定收取，随部署交易的 value 一起付给平台收款地址，部署成功即已付费，不需要另外转账。常见价格：BNB Chain 0.05 BNB、Ethereum 0.02 ETH、Arbitrum One 0.02 ETH、Base 0.02 ETH、Polygon 50 POL、Linea 0.01 ETH；价目表之外的链按 1 个原生币计。链上 gas 需要自己另外准备。',
      },
      {
        q: '代币合约归谁？',
        a: '归你。合约由你的钱包直接部署，表单里填的管理员地址就是合约所有者，默认是你连接的钱包。平台不持有私钥、不托管资产，部署出来的合约地址会直接显示在页面上。',
      },
      {
        q: '支持哪几种代币模式？',
        a: '两种。标准代币包含名称、符号、精度、总量和所有者；纯手续费代币在此基础上增加营销税、销毁税和营销钱包。两种代币都由你的钱包直接部署。',
      },
      {
        q: '代币发完之后还能做什么？',
        a: '把合约地址填进下面的「代币工具」，可以放弃所有权（转给黑洞地址，操作不可撤销）。加池和交易请去这条链自己的 DEX。',
      },
      {
        q: '为什么这里没有靓号地址和代币管理？',
        a: '那几项依赖平台工厂合约和链的收录，而这个通道刻意不依赖任何平台合约。需要全套工具（靓号地址、代币管理、市值机器人）请去主站，用主站已收录的链创建代币。',
      },
    ],
    feeTableTitle: '常见链的部署费',
    feeTableNote:
      '这里只列出常见链；具体费用以连接钱包后页面显示为准。价目表之外的链按 1 个原生币计。',
    relatedTitle: '相关站点',
  },

  en: {
    /* 与英文 h1 同一个直答口径；搜索词比「Any-Chain Token Launch」更自然 */
    title: 'Create a Token on Any EVM Chain | GTokenTool',
    h1: 'Create a Token on Any EVM Chain',
    description:
      'Create an ERC-20-compatible token on any EVM chain directly from your wallet. Choose a standard or tax token, set the supply and fees, and deploy in one transaction. No factory contract required.',
    answer:
      'GTokenTool lets you create an ERC-20-compatible token on any EVM-compatible chain your wallet supports. The contract is deployed directly from your wallet, with no factory contract in between, so you can launch on new or niche chains. The per-chain deployment fee is included in the transaction; network gas is separate.',
    panelTitle: 'Steps and FAQs',
    factsTitle: 'At a glance',
    /* facts 只进 JSON-LD 的 featureList，不在页面上铺表（理由见中文那侧） */
    facts: [
      { label: 'Supported chains', value: 'Any EVM-compatible chain your wallet can switch to' },
      { label: 'How it deploys', value: 'Your wallet deploys directly; no factory required' },
      { label: 'Fee', value: 'Fixed per chain, paid as the value of the deployment transaction' },
      { label: 'Token modes', value: 'Standard / tax' },
      { label: 'Contract owner', value: 'The owner address you enter — your connected wallet by default' },
      { label: 'Source', value: 'Solidity 0.8.6 · MIT · copy source and constructor parameters from the page' },
    ],
    stepsTitle: 'How to create a token on any EVM chain',
    steps: [
      'Connect your wallet and switch it to the chain you want to launch on.',
      'Choose a token type and enter the name, symbol, decimals, total supply and owner address.',
      'If you choose Tax, add the marketing fee, burn fee and marketing wallet.',
      'Select “Create token” and confirm the transaction in your wallet. The deployment fee is included.',
      'Copy the contract address and add liquidity on that chain’s DEX.',
    ],
    faqTitle: 'Frequently asked questions',
    faq: [
      {
        q: 'Which chains can I launch a token on?',
        a: 'Any EVM-compatible chain. If your wallet can switch to it, you can deploy there. Because this page does not rely on a platform contract, chains the main site has not listed yet work just as well.',
      },
      {
        q: 'How much does it cost to launch a token?',
        a: 'The fee is fixed per chain and is paid as the value of the deployment transaction, so a successful deploy means it is already paid — there is no separate transfer. Common prices: 0.05 BNB on BNB Chain; 0.02 ETH on Ethereum, Arbitrum One and Base; 50 POL on Polygon; 0.01 ETH on Linea. Chains outside the price list are charged 1 native coin. Network gas comes on top of that.',
      },
      {
        q: 'Who owns the token contract?',
        a: 'You do. The contract is deployed from your own wallet, and the owner address you enter becomes its owner — your connected wallet by default. The platform never holds private keys or your assets, and the new contract address shows up on the page as soon as it is deployed.',
      },
      {
        q: 'Which token types are available?',
        a: 'There are two. Standard tokens have a name, symbol, decimals, supply and owner. Tax tokens add marketing and burn fees plus a marketing wallet. Both are deployed directly from your wallet.',
      },
      {
        q: 'What can I do after the token is deployed?',
        a: 'Paste the contract address into the token tools below to renounce ownership; ownership is sent to the dead address and cannot be restored. Add liquidity and trade on that chain’s DEX.',
      },
      {
        q: 'Can I create a vanity address or manage the token here?',
        a: 'No. Those tools require a platform factory contract and a supported chain. This page deliberately works without a factory so it can serve any EVM chain. For vanity addresses, token management or a volume bot, use the main site and launch on a supported chain.',
      },
    ],
    feeTableTitle: 'Deployment fee by chain',
    feeTableNote:
      'This table shows the most common chains. The page displays the exact fee for your connected chain; chains outside the price list are charged 1 native coin.',
    relatedTitle: 'Related sites',
  },
}

/**
 * 从 `/en/` 路径或旧的 `?lang=en` 查询串里取语言。
 * 查询串只保留兼容性；新页面、canonical 与 hreflang 都用独立路径。
 */
export function preferredLocale(search, pathname = '') {
  if (/\/en\/?$/.test(pathname || '')) return 'en'
  const match = /[?&]lang=([A-Za-z-]+)/.exec(search || '')
  const raw = match ? match[1].toLowerCase() : ''
  if (raw.startsWith('en')) return 'en'
  if (raw.startsWith('zh')) return 'zh'
  return SITE.defaultLocale
}

/** 页面 / 结构化数据都按这个口径取当前语言的一整份文案 */
export function contentFor(locale) {
  return locale === 'en' ? CONTENT.en : CONTENT.zh
}

/** 某语言的地址（默认语言在根路径，英文在独立的 /en/ 路径） */
export function urlFor(locale) {
  if (locale === SITE.defaultLocale) return `${SITE.origin}/`
  return `${SITE.origin}/${locale}/`
}
